import React, { forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import Dropdown from 'antd/es/dropdown';
import Menu from 'antd/es/menu';

import Button, { EButtonColors } from 'components/Button';
import useApi from 'hooks/useAPI';
import ExitIcon from 'assets/images/exit.svg';
import { useDeleteImpersonationMutation } from 'redux/apiSlices/user.slice';
import { useSignOutMutation } from 'redux/apiSlices/auth.slice';
import { useAppDispatch, useAppSelector, clearAllCachedData } from 'redux/store';
import usersSlice from 'redux/slice/users.slice';
import toast from 'components/Notification';
import { useIsMobile } from 'hooks/useIsMobile';
import { RoutesNames } from '../../RoutesNames';

export type TAppBarHandle = {
  logout: () => Promise<void>;
};

type TAppBarProps = {
  handleMenuClick: () => void;
};

const AppBar = forwardRef<TAppBarHandle, TAppBarProps>(({ handleMenuClick }, ref) => {
  const { users, userType, impersonateOn } = useAppSelector((state) => state.usersSlice);
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile(1024);
  const history = useHistory();
  const { pagination } = useApi();
  const [deleteImpersonation, { isLoading }] = useDeleteImpersonationMutation();
  const [signOut] = useSignOutMutation();

  const logOut = async () => {
    if (impersonateOn) {
      try {
        await deleteImpersonation().unwrap();
      } catch (e) {
        console.warn('Failed to clear impersonation on logout:', e);
      }
    }
    dispatch(usersSlice.actions.resetUsersState());
    // Clear cached data before signOut — for federated sessions, Auth.signOut()
    // redirects the browser to Cognito /logout automatically (via oauth config)
    clearAllCachedData();
    try {
      await signOut().unwrap();
    } catch (e) {
      // For federated (SSO) sessions, Auth.signOut() redirects the browser
      // and the promise may reject with 'Signout timeout fail' — this is expected
      console.error('Logout:', e);
    }
    // For non-federated sessions (federated sessions already redirected via Cognito)
    history.push(RoutesNames.SignIn);
  };

  useImperativeHandle(ref, () => ({
    logout: logOut,
  }));

  const removeImpersonation = async () => {
    try {
      await deleteImpersonation().unwrap();
    } catch (e) {
      toast({ type: 'error', description: 'Something went wrong!' });
    }
    if (window.opener) {
      window.close();
    }
  };

  const profile = userType ? users?.[userType] : null;
  const isTenant = profile && 'type' in profile && (profile.type as string) === 'tenant';

  const dropdownOverlay = (
    <SDropdownMenu>
      {isTenant && (
        <Menu.Item key='payment-settings' onClick={() => pagination.setPage(0)}>
          <Link to='/payment-settings'>Payment settings</Link>
        </Menu.Item>
      )}
      <Menu.Item
        key='logout'
        data-testid='logOut'
        onClick={() => {
          pagination.setPage(0);
          logOut();
        }}
      >
        Log out
      </Menu.Item>
    </SDropdownMenu>
  );

  return (
    <SHeader>
      <SToolbar>
        {isMobile && (
          <SIconButton onClick={handleMenuClick} aria-label='menu'>
            <SMenuSvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
              <path d='M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z' />
            </SMenuSvg>
          </SIconButton>
        )}
        <SButtonsWrapper>
          {impersonateOn && profile && 'propertyManagementName' in profile && (
            <SExitButton onClick={removeImpersonation} size='small' color={EButtonColors.Default} disabled={isLoading}>
              <SExitIcon src={ExitIcon} alt='' />
              Exit {profile.propertyManagementName} view
            </SExitButton>
          )}
          <SIconsContainer>
            <SIconButton aria-label='profile'>
              <Link to='/profile'>
                {profile && 'profilePicture' in profile && profile.profilePicture ? (
                  <SPhoto src={profile.profilePicture} alt='profile pic' />
                ) : (
                  <SAccountSvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
                    <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z' />
                  </SAccountSvg>
                )}
              </Link>
            </SIconButton>
            {!isMobile && (
              <Dropdown
                overlay={dropdownOverlay}
                placement='bottomCenter'
                trigger={['click']}
                overlayStyle={{ borderRadius: 24 }}
              >
                <SIconButton aria-label='user menu' data-testid='userdropdown'>
                  <SChevronSvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z' />
                  </SChevronSvg>
                </SIconButton>
              </Dropdown>
            )}
          </SIconsContainer>
        </SButtonsWrapper>
      </SToolbar>
    </SHeader>
  );
});

AppBar.displayName = 'AppBar';

export default AppBar;

const SHeader = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 100;
  background: #fcc425;
  box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
    0px 1px 10px 0px rgba(0, 0, 0, 0.12);

  @media (max-width: 1024px) {
    display: flex;
    justify-content: center;
  }
`;

const SToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 16px;
  height: 64px;
  width: 100%;

  @media (max-width: 1024px) {
    justify-content: space-between;
  }
`;

const SButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`;

const SIconsContainer = styled.div`
  display: flex;
`;

const SIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 12px;
  border-radius: 50%;

  &:hover {
    background: transparent;
  }

  @media (max-width: 960px) {
    padding: 0;
  }
`;

const SMenuSvg = styled.svg`
  width: 24px;
  height: 24px;
  fill: #0d0d0d;
`;

const SChevronSvg = styled.svg`
  width: 24px;
  height: 24px;
  fill: #0d0d0d;
`;

const SPhoto = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;

  @media (max-width: 1024px) {
    margin-left: 20px;
  }
`;

const SAccountSvg = styled.svg`
  width: 24px;
  height: 24px;
  fill: #0d0d0d;
  display: flex;

  @media (max-width: 1024px) {
    margin-left: 20px;
  }
`;

const SExitIcon = styled.img`
  margin-right: 8px;
`;

const SExitButton = styled(Button)`
  &&& {
    background: #ffffff;
    border-color: #ffffff;
    color: #0d0d0d;
    padding: 0px 24px;

    &:hover,
    &:focus {
      background: #ffffff;
      border-color: #ffffff;
      color: #0d0d0d;
    }

    &:disabled {
      background: #ffffff;
      border-color: #ffffff;
      color: #0d0d0d;
      opacity: 0.45;
    }
  }

  @media (max-width: 1024px) {
    padding: 4px 16px;
  }
`;

const SDropdownMenu = styled(Menu)`
  &.ant-dropdown-menu {
    border-radius: 24px;
    width: 219px;
    padding: 0px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  .ant-dropdown-menu-item {
    padding: 6px 24px;
    border-radius: 4px;
    &:first-child {
      padding: 12px 24px 6px;
    }
    &:last-child {
      padding: 6px 24px 12px;
    }
  }
`;
